// KPI page entry dialogs: add or remove paid seats, log hours, set goals.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Trash2 } from 'lucide-react';
import type { KpiGoals, KpiMeta, KpiTimeEntry, SeatChangeRequest, TimeCategory, TimeEntryRequest } from '@upm/domain';
import { api, ApiError } from '../../lib/api';
import { fmtCalendarDate } from '../../lib/format';
import { Banner, Button, Dialog, Select, TextField, cx } from '../../ui';

type Firms = KpiMeta['firms'];

/** Runs one save: blocks a second click while it is in flight and turns an API error into a line of text. */
function useSave(onSaved: () => Promise<void>, onClose: () => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const run = async (fn: () => Promise<unknown>, close = true) => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError(null);
    try {
      await fn();
      await onSaved();
      if (close) onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'We couldn’t save that. Check your connection and try again.');
    } finally { inFlight.current = false; setBusy(false); }
  };
  return { busy, error, setError, run };
}

const FirmSelect = ({ firms, value, onChange }: { firms: Firms; value: string; onChange: (v: string) => void }) => (
  <Select label="Firm" value={value} onChange={(e) => onChange(e.target.value)} required>
    <option value="">Choose a firm</option>
    {firms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
  </Select>
);

// ---------------------------------------------------------------- seat change

export function SeatChangeDialog({ open, onClose, onSaved, firms, today }: {
  open: boolean; onClose: () => void; onSaved: () => Promise<void>; firms: Firms; today: string;
}) {
  const [firmId, setFirmId] = useState('');
  const [direction, setDirection] = useState<'add' | 'remove'>('add');
  const [seats, setSeats] = useState('');
  const [on, setOn] = useState(today);
  const [note, setNote] = useState('');
  const { busy, error, setError, run } = useSave(onSaved, onClose);
  useEffect(() => { if (open) { setFirmId(''); setDirection('add'); setSeats(''); setOn(today); setNote(''); setError(null); } }, [open, today, setError]);

  const submit = () => {
    const n = Number(seats);
    if (!firmId) return setError('Choose the firm.');
    if (!Number.isInteger(n) || n < 1) return setError('Enter how many seats, as a whole number.');
    const body: SeatChangeRequest = { firmId, change: direction === 'add' ? n : -n, effectiveOn: on, ...(note.trim() ? { note: note.trim() } : {}) };
    void run(() => api.post('/api/admin/kpis/seat-changes', body));
  };
  return (
    <Dialog open={open} title="Seat change" onClose={busy ? () => undefined : onClose} footer={
      <>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>Save seat change</Button>
      </>
    }>
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <FirmSelect firms={firms} value={firmId} onChange={setFirmId} />
        <fieldset className="m-0 flex gap-2 border-0 p-0">
          <legend className="mb-1.5 p-0 text-sm font-semibold">What changed?</legend>
          {(['add', 'remove'] as const).map((d) => (
            <label key={d} className={cx('flex flex-1 cursor-pointer items-center gap-2.5 rounded-[10px] px-3.5 py-2.5 text-sm', direction === d ? 'border-[1.5px] border-brand bg-brand-tint font-semibold' : 'border border-line-btn bg-white')}>
              <input type="radio" name="seat-direction" className="h-[18px] w-[18px] accent-brand" checked={direction === d} onChange={() => setDirection(d)} />
              {d === 'add' ? 'Seats added' : 'Seats removed'}
            </label>
          ))}
        </fieldset>
        <div className="grid grid-cols-2 gap-4">
          <TextField label="Seats" type="number" inputMode="numeric" min={1} max={1000} step={1} value={seats} onChange={(e) => setSeats(e.target.value)} required />
          <TextField label="Effective" type="date" max={today} value={on} onChange={(e) => setOn(e.target.value)} required />
        </div>
        <TextField label="Note" optional value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        {error && <Banner tone="error" role="alert">{error}</Banner>}
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------- log hours

export function LogHoursDialog({ open, onClose, onSaved, firms, today, recent }: {
  open: boolean; onClose: () => void; onSaved: () => Promise<void>; firms: Firms; today: string; recent: KpiTimeEntry[];
}) {
  const [firmId, setFirmId] = useState('');
  const [category, setCategory] = useState<TimeCategory>('onboarding');
  const [hours, setHours] = useState('');
  const [on, setOn] = useState(today);
  const [note, setNote] = useState('');
  const { busy, error, setError, run } = useSave(onSaved, onClose);
  useEffect(() => { if (open) { setFirmId(''); setCategory('onboarding'); setHours(''); setOn(today); setNote(''); setError(null); } }, [open, today, setError]);

  const submit = () => {
    const h = Number(hours);
    if (!firmId) return setError('Choose the firm.');
    if (!(h >= 0.25 && h <= 24)) return setError('Enter hours between 0.25 and 24.');
    const body: TimeEntryRequest = { firmId, category, hours: h, entryOn: on, ...(note.trim() ? { note: note.trim() } : {}) };
    void run(() => api.post('/api/admin/kpis/time-entries', body));
  };
  return (
    <Dialog open={open} title="Log hours" onClose={busy ? () => undefined : onClose} footer={
      <>
        <Button onClick={onClose} disabled={busy}>Close</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>Save hours</Button>
      </>
    }>
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <FirmSelect firms={firms} value={firmId} onChange={setFirmId} />
        <Select label="Kind of work" value={category} onChange={(e) => setCategory(e.target.value as TimeCategory)}>
          <option value="onboarding">Onboarding (counts toward hours per seat)</option>
          <option value="support">Support after go-live</option>
        </Select>
        <div className="grid grid-cols-2 gap-4">
          <TextField label="Hours" type="number" inputMode="decimal" min={0.25} max={24} step={0.25} value={hours} onChange={(e) => setHours(e.target.value)} required />
          <TextField label="Day" type="date" max={today} value={on} onChange={(e) => setOn(e.target.value)} required />
        </div>
        <TextField label="Note" optional value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        {error && <Banner tone="error" role="alert">{error}</Banner>}
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
      {recent.length > 0 && (
        <section aria-label="Recent hours">
          <h3 className="m-0 mb-1 text-[13px] font-bold uppercase tracking-[0.06em] text-muted">Recent hours</h3>
          <ul className="m-0 list-none p-0">
            {recent.map((t) => (
              <li key={t.id} className="flex items-center gap-3 border-t border-line-soft py-2 text-sm first:border-t-0">
                <span className="w-20 flex-none text-muted">{fmtCalendarDate(t.on).replace(/, \d{4}$/, '')}</span>
                <span className="min-w-0 flex-1 truncate">{t.firmName}<span className="text-muted"> · {t.category === 'support' ? 'Support' : 'Onboarding'} · {t.by}</span></span>
                <span className="tabular-nums">{t.hours} h</span>
                <button type="button" aria-label={`Remove ${t.hours} hours for ${t.firmName} on ${fmtCalendarDate(t.on)}`} disabled={busy}
                  onClick={() => void run(() => api.del(`/api/admin/kpis/time-entries/${t.id}`), false)}
                  className="flex h-8 w-8 flex-none items-center justify-center rounded-lg border-0 bg-transparent text-muted hover:bg-panel hover:text-bad">
                  <Trash2 aria-hidden size={16} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------- goals

const num = (s: string) => (s.trim() === '' ? null : Number(s));

export function GoalsDialog({ open, onClose, onSaved, goals, today }: {
  open: boolean; onClose: () => void; onSaved: () => Promise<void>; goals: KpiGoals; today: string;
}) {
  const [seats, setSeats] = useState('');
  const [from, setFrom] = useState('');
  const [by, setBy] = useState('');
  const [hps, setHps] = useState('');
  const [ret, setRet] = useState('');
  const [reply, setReply] = useState('');
  const { busy, error, setError, run } = useSave(onSaved, onClose);
  useEffect(() => {
    if (!open) return;
    setSeats(goals.paidSeats ? String(goals.paidSeats.target) : ''); setFrom(goals.paidSeats?.startOn ?? today); setBy(goals.paidSeats?.targetOn ?? '');
    setHps(goals.hoursPerSeat ? String(goals.hoursPerSeat.target) : ''); setRet(goals.retention ? String(goals.retention.target) : '');
    setReply(goals.firstReplyHours ? String(goals.firstReplyHours.target) : ''); setError(null);
  }, [open, goals, today, setError]);

  const submit = () => {
    const [s, h, r, f] = [num(seats), num(hps), num(ret), num(reply)];
    if (s !== null && (!Number.isInteger(s) || s < 1)) return setError('Paid seats goal: enter a whole number.');
    if (s !== null && (!from || !by || by <= from)) return setError('Paid seats goal: choose a start date and a later goal date.');
    if (h !== null && !(h >= 0.1)) return setError('Hours per seat: enter a number above 0.');
    if (r !== null && !(r >= 1 && r <= 100)) return setError('Retention: enter a percent from 1 to 100.');
    if (f !== null && !(f >= 0.1)) return setError('First reply: enter hours above 0.');
    const body: KpiGoals = {
      paidSeats: s === null ? null : { target: s, startOn: from, targetOn: by },
      hoursPerSeat: h === null ? null : { target: h },
      retention: r === null ? null : { target: r },
      firstReplyHours: f === null ? null : { target: f },
    };
    void run(() => api.put('/api/admin/kpis/goals', body));
  };
  const group = (title: string, children: ReactNode) => (
    <fieldset className="m-0 flex flex-col gap-3 rounded-[10px] border border-line p-4">
      <legend className="px-1 text-sm font-bold">{title}</legend>{children}
    </fieldset>
  );
  return (
    <Dialog open={open} wide title="Edit goals" onClose={busy ? () => undefined : onClose} footer={
      <>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>Save goals</Button>
      </>
    }>
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); submit(); }}>
        <p className="m-0 text-sm text-ink-2">Leave a goal empty to have no goal for it.</p>
        {group('Paid seats', (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <TextField label="Paid seats" type="number" inputMode="numeric" min={1} step={1} value={seats} onChange={(e) => setSeats(e.target.value)} />
            <TextField label="Counting from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            <TextField label="By" type="date" value={by} onChange={(e) => setBy(e.target.value)} />
          </div>
        ))}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <TextField label="Hours per seat, at most" type="number" inputMode="decimal" min={0.1} step={0.1} value={hps} onChange={(e) => setHps(e.target.value)} />
          <TextField label="30-day retention, at least (%)" type="number" inputMode="numeric" min={1} max={100} step={1} value={ret} onChange={(e) => setRet(e.target.value)} />
          <TextField label="First reply to a bug, at most (hours)" type="number" inputMode="decimal" min={0.1} step={0.5} value={reply} onChange={(e) => setReply(e.target.value)} />
        </div>
        {error && <Banner tone="error" role="alert">{error}</Banner>}
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}
